import { generatePath, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Container,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/PriorityHigh';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CouponDispType } from '../../../../user-api/dist/src/entity/cx-coupon.entity';
import { xuseCouponAct } from '../../app/actions';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { MyFooter } from '../footer/MyFooter';
import { MyAppBar } from '../appbar/MyAppBar';
import { appPath } from '../../router/AppRoute';
import { CouponUseWhen } from '../../../../user-api/dist/src/entity/cx-coupon.entity';
import { colorUtil } from '../../util/ColorUtil';
import i18n from '../../i18n';
import DATA_QA from '../../util/DataQaAttr';
import ExtraLink from './ExtraLink';

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    container: {
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    couponImageDiv: {
      height: '75vw',
      width: '75vw',
      minWidth: '75vw',
      paddingTop: '100%',
      backgroundSize: 'contain',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
    bottomBox: {
      height: '10vh',
    },
    titleCoupon: {
      fontSize: '32px',
      fontWeight: 'bold',
      padding: '26px 38px',
      lineHeight: '24px',
      '@media screen and (max-width: 560px)': {
        padding: '26px 25px',
      },
    },
    titleImage: {
      fontSize: '18px',
      fontWeight: 'bold',
    },
    subTitleImage: {
      fontSize: '15px',
      fontWeight: 'normal',
      overflowWrap: 'break-word',
      width: '100%',
    },
    commonWidthBox: {
      width: '100%',
      maxWidth: 'calc(75vw + 64px)',
    },
    titleWarningIcon: {
      minWidth: 30,
      height: 30,
      borderRadius: '50%',
      backgroundColor: colorUtil.red90,
      color: colorUtil.white,
      marginRight: 12,
    },
    titleWarningText: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: colorUtil.red90,
      whiteSpace: 'pre-line',
      lineHeight: '1.7',
    },
    cautionBox: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colorUtil.red90,
      color: colorUtil.white,
      borderRadius: 10,
      padding: '18px',
    },
  });
});

function ShowMessage(props: { message: string }): JSX.Element {
  const { message } = props;

  return (
    <Box
      display="flex"
      position="fixed"
      left="0"
      top="0"
      right="0"
      bottom="0"
      alignItems="center"
      mx={5}
    >
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justify="center"
      >
        <Typography style={{ fontSize: '1.1rem' }} variant="h6">
          {message}
        </Typography>
      </Grid>
    </Box>
  );
}

export function Coupon(props: {}): JSX.Element {
  const { t } = useTranslation();
  const dateTimeformart =
    i18n.language === 'ja' ? 'MM月DD日 HH時mm分ss秒' : 'MM/DD HH[h]mm[m]ss[s]';
  const timeFormart =
    i18n.language === 'ja' ? 'HH時間mm分ss秒' : 'HH[h]mm[m]ss[s]';
  const branch = useAppSelector((state) => {
    return state.inputAnswer.session.cxHttpRes!.data!.cxCouponIssued!
      .cxShopToken!.branch!;
  });

  const coupon = useAppSelector((state) => {
    return state.inputAnswer.session.cxHttpRes!.data!.cxCouponIssued!.cxCoupon!;
  });

  const classes = useStyles();

  const dispatch = useAppDispatch();

  const couponIssued = useAppSelector((state) => {
    return state.inputAnswer.session.cxHttpRes!.data!.cxCouponIssued!;
  });

  const [open, setOpen] = useState(false);

  const [expired, setExpired] = useState(
    couponIssued.hideAt ? new Date(couponIssued.hideAt) <= new Date() : false,
  );
  const [started, setStarted] = useState(
    new Date() >= new Date(couponIssued.beginAt),
  );
  // Prevent an error message showing when end date coupon ended but expired date coupon still available
  const [ended, setEnded] = useState(
    new Date() > new Date(couponIssued.endExAt) && expired,
  );
  const [hideAtStr, setHideAtStr] = useState('');
  const [nowStr, setNowStr] = useState('');
  const [restStr, setRestStr] = useState('');

  const used = !!couponIssued.usedAt;

  const beginAtMoment = moment(couponIssued.beginAt).millisecond(0);
  const endExAtMoment = moment(couponIssued.endExAt).millisecond(0);

  const rrHistory = useHistory();

  useEffect(() => {
    // react-routerを使うまでは暫定で強制的にURLを書き換える
    // ブラウザBACKされた上でブックマークされると救済できないがレアケースなので見送る
    const couponPath = generatePath(appPath.coupon.show);

    if (!window.location.pathname.endsWith(couponPath)) {
      // console.log(`replace path`);

      rrHistory.replace(`${couponPath}?t=${couponIssued.couponToken}`);
    }
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const nowMoment = moment().millisecond(0);
      const hideAtMoment = moment(couponIssued.hideAt!).millisecond(0);

      if (!expired) {
        if (hideAtMoment.diff(nowMoment, `seconds`) <= 0) {
          setExpired(true);
        }
      }

      // クーポンの利用開始可能日が始まっているか
      if (!started) {
        if (nowMoment.diff(beginAtMoment, `seconds`) >= 0) {
          setStarted(true);
        }
      }

      // クーポンの利用期限が過ぎていないか
      if (!ended) {
        if (nowMoment.diff(endExAtMoment, `seconds`) >= 0 && expired) {
          setEnded(true);
        }
      }

      setHideAtStr(hideAtMoment.format(dateTimeformart));

      setNowStr(nowMoment.format(dateTimeformart));

      let restMsec = hideAtMoment.diff(nowMoment, 'milliseconds');

      if (restMsec <= 0) {
        setRestStr(i18n.language === 'ja' ? '00時間00分00秒' : '00h00m00s');
      } else {
        setRestStr(moment.utc(restMsec).format(timeFormart));
      }
    }, 100);

    return () => clearInterval(id);
  }, [
    expired,
    setExpired,
    started,
    setStarted,
    ended,
    setEnded,
    couponIssued,
    beginAtMoment,
    endExAtMoment,
  ]);

  // 使用済みでないかチェック
  if (expired) {
    return (
      <>
        <ShowMessage
          message={t('question.this_coupon_has_already_been_used')}
        />
        <MyFooter styles={{ position: 'absolute', bottom: '0' }} />
      </>
    );
  }

  // 有効期限が過ぎているかチェック
  if (ended) {
    return (
      <>
        <ShowMessage message={t('question.this_coupon_has_already_expired')} />
        <MyFooter styles={{ position: 'absolute', bottom: '0' }} />
      </>
    );
  }

  // 2022/05/19仕様変更。有効期限が始まっていない場合も通常時と同じ画面を表示しメッセージを変更する
  // 有効期限が始まっているかチェック
  // if (!started) {
  //   return <ShowMessage message={`このクーポンの有効期限はまだ始まっていません。クーポンに記載されている有効期限をご確認ください`} />
  // }

  let dispSpan: string;
  if (couponIssued.dispType === CouponDispType.MIN) {
    dispSpan = `${couponIssued.dispSpan}`;
  } else {
    dispSpan = t('common.within_today');
  }

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleUse = () => {
    const couponToken = couponIssued.couponToken;
    dispatch(xuseCouponAct({ couponToken }));

    return false;
  };

  return (
    <Container
      maxWidth={false}
      disableGutters={true}
      className={classes.container}
    >
      <MyAppBar />
      <Box position="relative" flexGrow="1">
        {!used && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mt={4}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              className={classes.commonWidthBox}
            >
              {/* Icon */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                className={classes.titleWarningIcon}
              >
                <ErrorIcon />
              </Box>

              {/* Text */}
              <Box>
                <Typography className={classes.titleWarningText}>
                  {t('common.warning_get_coupon_message.message')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        {/* 冒頭メッセージ(通常) begin */}
        {used && !!coupon.usage && (
          <Box mt={4}>
            <Typography variant="h5" align="center">
              <Box fontWeight="fontWeightBold">{coupon.usage}</Box>
            </Typography>
          </Box>
        )}
        {/* 冒頭メッセージ(通常) end */}

        {/* 冒頭メッセージ(利用可能時刻以前) begin */}
        {!started && (
          <Box mt={4} mx={2}>
            <Typography variant="h5" align="left">
              <Box fontWeight="fontWeightBold">
                {`${t('question.can_be_used_from', {
                  date: beginAtMoment.format(dateTimeformart),
                })}`}{' '}
                ({t('question.recommend_bookmarking_this_page')})
              </Box>
            </Typography>
            {/* <Typography variant="h5" align="left">
            <Box fontWeight="fontWeightBold" mt={1}>※ブラウザにブックマークをお勧めします</Box>
          </Typography> */}
          </Box>
        )}
        {/* 冒頭メッセージ(利用可能時刻以前) end */}
        {/* クーポンエリア begin */}
        <Box display="flex" justifyContent="center" mt={4}>
          <Card>
            <Box m={4} position="relative">
              {/* クーポン画像 begin */}
              <Box
                position="relative"
                className={classes.couponImageDiv}
                style={{
                  backgroundImage: `url(${coupon.psImageUrl})`,
                }}
              >
                {/* Copyガード begin */}
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  width="100%"
                  height="100%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  &nbsp;
                </Box>
                {/* Copyガード end */}

                {/* 見本表示中の場合 */}
                {!used && (
                  <>
                    {/* グレー begin */}
                    <Box
                      position="absolute"
                      left={0}
                      top={0}
                      width="100%"
                      height="100%"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      bgcolor="black"
                      style={{
                        opacity: 0.4,
                      }}
                    ></Box>
                    {/* グレー end */}
                    {/* fade container begin */}
                    <Box
                      position="absolute"
                      left={0}
                      top={0}
                      width="100%"
                      height="100%"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      bgcolor={colorUtil.black4}
                    >
                      {/* 「クーポン見本」container begin */}
                      <Box
                        borderRadius="100px"
                        bgcolor={colorUtil.couponSample}
                      >
                        {/* 「クーポン見本」 begin */}
                        <Typography className={classes.titleCoupon}>
                          {t('question.sample_coupon')}
                        </Typography>
                        {/* 「クーポン見本」 end */}
                      </Box>
                      {/* 「見本 クーポン」container end */}
                    </Box>
                    {/* fade container end */}
                  </>
                )}
              </Box>
              {/* クーポン画像 end */}
            </Box>
          </Card>
        </Box>
        {/* クーポンエリア end */}

        {/* 注意事項 begin */}
        {!used && (
          <Box
            id="coupon-caution"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              whiteSpace: 'pre-line',
              width: '100%',
              mt: 2.5,
            }}
          >
            <Box className={`${classes.cautionBox} ${classes.commonWidthBox}`}>
              <Typography
                style={{ marginBottom: 10 }}
                variant="h6"
                className={classes.titleImage}
              >
                {t('question.terms_and_conditions')}
              </Typography>
              <Typography variant="subtitle1" className={classes.subTitleImage}>
                {coupon.caution}
              </Typography>
            </Box>
          </Box>
        )}
        {/* 注意事項 end */}

        {!used && (
          <>
            {/* ブラウザブックマーク催促 begin */}
            {coupon.useWhen === CouponUseWhen.LATER && (
              <Box display="flex" justifyContent="center" mt={4}>
                <Box mt={1} mx={2}>
                  <Typography variant="h5" align="left">
                    <Box color="red" fontWeight="fontWeightBold">
                      {t('question.recommend_bookmarking_this_page')}
                    </Box>
                  </Typography>
                </Box>
              </Box>
            )}
            {/* ブラウザブックマーク催促 end */}

            {/* 使用するButton begin */}
            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleClickOpen}
                style={{ fontWeight: 'bold' }}
                data-qa={DATA_QA.BUTTON.USE_COUPON}
              >
                {t('question.use_coupon')}
              </Button>
              {/* 使用の問い合わせダイアログ begin */}
              {started && (
                <Dialog
                  open={open}
                  onClose={handleClose}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                >
                  <DialogTitle id="alert-dialog-title">
                    {t('question.wanna_use_coupon')}
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                      {t('question.this_coupon_can_only_be_used_once')}
                      <Box component="span" color="red" fontWeight="bold">
                        &nbsp;
                        {t('question.cannot_be_used_again_after_displaying')}
                      </Box>
                      <br />
                      <br />
                      {t(
                        'question.the_display_time_is_n_minutes_please_show_to_staff_within_n_minutes',
                        { minutes: dispSpan },
                      )}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleClose}
                      color="secondary"
                      variant="outlined"
                    >
                      {t('question.btn.dont_use')}
                    </Button>
                    <Button
                      onClick={handleUse}
                      color="primary"
                      variant="contained"
                      autoFocus
                      data-qa={DATA_QA.BUTTON.MODAL_USE_COUPON}
                    >
                      {t('question.btn.use')}
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
              {/* 使用の問い合わせダイアログ end */}

              {/* 使用の問い合わせダイアログ begin */}
              {!started && (
                <Dialog
                  open={open}
                  onClose={handleClose}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                >
                  <DialogTitle id="alert-dialog-title">{``}</DialogTitle>
                  <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                      {t('question.cannot_be_used_yet')}
                      <br />
                      {t('question.try_again_after', {
                        date: beginAtMoment.format(dateTimeformart),
                      })}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button
                      onClick={handleClose}
                      color="secondary"
                      variant="outlined"
                    >
                      {t('question.btn.close')}
                    </Button>
                  </DialogActions>
                </Dialog>
              )}
              {/* 使用の問い合わせダイアログ end */}
            </Box>
            {/* 使用するButton end */}
          </>
        )}

        {used && (
          <>
            {/* 有効期限 begin */}
            <Box mt={4} mx={3}>
              <Box mb={2}>
                <Typography variant="h5" align="center">
                  <Box fontWeight="fontWeightBold">
                    {t('question.remaining_date', { date: restStr })}
                  </Box>
                </Typography>
              </Box>

              <Typography variant="h6" align="center">
                <Box fontWeight="fontWeightBold">
                  {t('question.current_time')} {nowStr}
                </Box>
              </Typography>
              <Typography variant="h6" align="center">
                <Box fontWeight="fontWeightBold">
                  {t('question.valid_until')} {hideAtStr}
                </Box>
              </Typography>
            </Box>
            <ExtraLink sx={{ mt: 4, mx: 3 }} />
            {/* 有効期限 end */}
          </>
        )}

        {/* 最下部余白 begin */}
        <Box className={classes.bottomBox}>&nbsp;</Box>
        {/* 最下部余白 end */}
      </Box>
      <MyFooter />
    </Container>
  );
}
